import React, { useState } from 'react';
import { isAnyWearableConnected } from '@/config/wearableProviders';
import { getApiUrl } from '@/config/api';
import InviteUserModal from '@/components/modals/InviteUserModal';

interface MembersTabProps {
  selectedTeam: any;
  isDark: boolean;
  onMemberRemoved?: () => void;
  readOnly?: boolean;
  currentUsername?: string;
}

const MembersTab: React.FC<MembersTabProps> = ({ selectedTeam, isDark, onMemberRemoved, readOnly = false, currentUsername }) => {
    const [inviteRole, setInviteRole] = useState<'athlete' | 'coach' | null>(null);
    const [removingId, setRemovingId] = useState<number | null>(null);
    const [confirmId, setConfirmId] = useState<number | null>(null);
    const [removeError, setRemoveError] = useState<string | null>(null);
    const [successMessage, setSuccessMessage] = useState<string | null>(null);
    const [hiddenIds, setHiddenIds] = useState<Set<number>>(new Set());

    const handleRemoveMember = async (userId: number) => {
        setRemovingId(userId);
        setRemoveError(null);
        setSuccessMessage(null);
        setConfirmId(null);

        try {
            const token = localStorage.getItem('access');
            const res = await fetch(getApiUrl(`/api/teams/teams/${selectedTeam.id}/remove-member/`), {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    ...(token ? { Authorization: `Bearer ${token}` } : {}),
                },
                body: JSON.stringify({ user_id: userId }),
            });
            if (!res.ok) {
                const data = await res.json().catch(() => ({}));
                throw new Error(data.error || `Failed to remove member (${res.status})`);
            }
            // Hide the member and show success message
            setHiddenIds(prev => new Set(prev).add(userId));
            setSuccessMessage('Member removed successfully. Changes may take a few minutes to appear across the dashboard.');
            // Sync backend state in the background
            onMemberRemoved?.();
        } catch (err) {
            setRemoveError(err instanceof Error ? err.message : 'An error occurred');
        } finally {
            setRemovingId(null);
        }
    };

    const renderRemoveButton = (member: any) => (
        <>
            {confirmId === member.id ? (
                <div className="flex items-center space-x-1">
                    <button
                        onClick={() => handleRemoveMember(member.id)}
                        disabled={removingId === member.id}
                        className="px-2 py-1 text-xs font-medium text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-900/20 rounded hover:bg-red-100 dark:hover:bg-red-900/40 transition-colors disabled:opacity-50"
                    >
                        {removingId === member.id ? '...' : 'Confirm'}
                    </button>
                    <button
                        onClick={() => { setConfirmId(null); setRemoveError(null); }}
                        className="px-2 py-1 text-xs font-medium cgm-text-secondary rounded hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                    >
                        Cancel
                    </button>
                </div>
            ) : (
                <button
                    onClick={() => { setConfirmId(member.id); setRemoveError(null); }}
                    className={`flex-shrink-0 flex items-center justify-center w-7 h-7 rounded-full border transition-colors ${isDark ? 'border-gray-600 hover:bg-red-900/30 hover:border-red-500/50 text-gray-400 hover:text-red-400' : 'border-gray-300 hover:bg-red-50 hover:border-red-300 text-gray-400 hover:text-red-500'}`}
                    title="Remove from team"
                >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 12H4" />
                    </svg>
                </button>
            )}
        </>
    );

    const renderMemberRow = (member: any, index: number) => {
        const isCurrentUser = currentUsername && member.username === currentUsername;
        return (
        <div key={member.id} className={`flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 p-3 sm:p-4 ${isCurrentUser ? (isDark ? 'bg-white/5 border-l-2 border-white/30' : 'bg-gray-900/5 border-l-2 border-gray-900/30') : (isDark ? 'bg-[#101010] hover:bg-[#1f1f1f]' : (index % 2 === 0 ? 'cgm-list-item' : 'cgm-list-item-alt'))}`}>
            <div className="flex items-center space-x-3 sm:space-x-4 min-w-0">
                <div className={`w-9 h-9 sm:w-10 sm:h-10 rounded-full flex-shrink-0 flex items-center justify-center font-semibold text-sm sm:text-base ${isCurrentUser ? (isDark ? 'bg-white text-gray-900' : 'bg-gray-900 text-white') : (isDark ? 'bg-gray-800 text-white' : 'bg-gray-200 text-gray-900')}`}>
                    {member.first_name ? member.first_name.charAt(0).toUpperCase() : member.username.charAt(0).toUpperCase()}
                </div>
                <div className="min-w-0">
                    <div className="font-semibold cgm-text-primary truncate">
                        {member.full_name || member.username}
                        {isCurrentUser && <span className="ml-2 text-xs cgm-text-secondary">(You)</span>}
                    </div>
                    <div className="text-sm cgm-text-secondary truncate">
                        {member.email}
                    </div>
                </div>
            </div>
            <div className="flex items-center justify-between sm:justify-end space-x-3 pl-12 sm:pl-0">
                <span className={`inline-flex items-center space-x-1 px-2 py-1 rounded-full text-xs font-medium whitespace-nowrap ${isAnyWearableConnected(member) ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300' : 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300'}`}>
                    <span className={`cgm-status-dot ${isAnyWearableConnected(member) ? 'cgm-status-connected' : 'cgm-status-warning'}`} />
                    <span>{isAnyWearableConnected(member) ? 'Connected' : 'Not Connected'}</span>
                </span>
                {!readOnly && renderRemoveButton(member)}
            </div>
        </div>
        );
    };

    return (
    <div className="space-y-4 sm:space-y-8">
        {removeError && (
            <div className="cgm-alert-error px-4 py-3 rounded-md text-sm">
                {removeError}
            </div>
        )}
        {successMessage && (
            <div className="cgm-alert-success px-4 py-3 rounded-md text-sm">
                {successMessage}
            </div>
        )}

        {/* Header */}
        <div className={`cgm-card rounded-2xl p-4 sm:p-6 border ${isDark ? 'border-gray-700/50' : 'border-gray-200/50'} shadow-xl`}>
            <h3 className="text-2xl sm:text-3xl font-bold cgm-text-primary mb-1 sm:mb-2">Team Members</h3>
            <p className="text-sm sm:text-lg cgm-text-secondary">All coaches and athletes in this team</p>
        </div>

        {/* Coaches Section */}
        <div className={`cgm-card rounded-2xl p-4 sm:p-6 border ${isDark ? 'border-gray-700/50' : 'border-gray-200/50'} shadow-xl`}>
            <div className="flex items-center justify-between">
                <div>
                    <h4 className="text-xl font-bold cgm-text-primary">Coaches ({selectedTeam.coaches.filter((c: any) => !hiddenIds.has(c.id)).length})</h4>
                    <p className="text-sm cgm-text-secondary">Coaching staff</p>
                </div>
                {!readOnly && (
                    <button
                        onClick={() => setInviteRole('coach')}
                        className={`flex items-center justify-center w-9 h-9 rounded-full border transition-colors ${isDark ? 'border-gray-600 hover:bg-gray-700 text-gray-300' : 'border-gray-300 hover:bg-gray-100 text-gray-600'}`}
                        title="Invite coach"
                    >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                        </svg>
                    </button>
                )}
            </div>

            {selectedTeam.coaches.filter((c: any) => !hiddenIds.has(c.id)).length > 0 ? (
                <div className="mt-6 space-y-2 sm:space-y-4">
                    {selectedTeam.coaches.filter((c: any) => !hiddenIds.has(c.id)).map((coach: any, index: number) => renderMemberRow(coach, index))}
                </div>
            ) : (
                <div className="text-center py-12 cgm-text-secondary">
                    <p>No coaches assigned to this team yet.</p>
                </div>
            )}
        </div>

        {/* Athletes Section */}
        <div className={`cgm-card rounded-2xl p-4 sm:p-6 border ${isDark ? 'border-gray-700/50' : 'border-gray-200/50'} shadow-xl`}>
            <div className="flex items-center justify-between">
                <div>
                    <h4 className="text-xl font-bold cgm-text-primary">Athletes ({selectedTeam.athletes.filter((a: any) => !hiddenIds.has(a.id)).length})</h4>
                    <p className="text-sm cgm-text-secondary">Team members</p>
                </div>
                {!readOnly && (
                    <button
                        onClick={() => setInviteRole('athlete')}
                        className={`flex items-center justify-center w-9 h-9 rounded-full border transition-colors ${isDark ? 'border-gray-600 hover:bg-gray-700 text-gray-300' : 'border-gray-300 hover:bg-gray-100 text-gray-600'}`}
                        title="Invite athlete"
                    >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                        </svg>
                    </button>
                )}
            </div>

            {selectedTeam.athletes.filter((a: any) => !hiddenIds.has(a.id)).length > 0 ? (
                <div className="mt-6 space-y-2 sm:space-y-4">
                    {selectedTeam.athletes.filter((a: any) => !hiddenIds.has(a.id)).map((athlete: any, index: number) => renderMemberRow(athlete, index))}
                </div>
            ) : (
                <div className="text-center py-12 cgm-text-secondary">
                    <p>No athletes assigned to this team yet.</p>
                </div>
            )}
        </div>

        {inviteRole && (
            <InviteUserModal
                onClose={() => setInviteRole(null)}
                defaultRole={inviteRole}
                onSuccess={onMemberRemoved}
            />
        )}
    </div>
    );
};

export default MembersTab;
